import '../styles/globals.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import MobileDetect from 'mobile-detect';
import type { AppProps } from 'next/app';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@zg-rentals/react-query5';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { useSiftScience } from '@zg-rentals/sift-science';

import { AppAuthBoundary, Auth, type User } from '../AuthProvider';
import { Head } from '@zg-rentals/next/pages';
import { ThemeConstellation } from '@zillow/constellation';
import { ToastProvider } from '@zillow/constellation-10';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';

import Layout from '../components/Layout';
import useScreenWidth from '../hooks/useScreenWidth';
import { AppContextProvider } from '../contexts/AppContextProvider';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent, trackUserEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';

const APP_NAME = 'payments-overview-web';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * (60 * 1000), // 5 mins
      retry: 0,
    },
  },
});

function SiftScience(props: { email: string; isImpersonator: boolean }) {
  useSiftScience(props);

  return null;
}

function AuthenticatedApp({ children, user }: { children: ReactNode; user: User }) {
  const isMobileDevice = typeof window !== 'undefined' && !!new MobileDetect(window.navigator.userAgent).phone();
  const { screenWidth } = useScreenWidth();

  const isPostingPathAdmin = user?.isPostingPathAdmin ?? false;
  const isImpersonator = (!!user?.impersonatorEmail && !!user?.impersonatorUserToken && isPostingPathAdmin) || false;

  if (process.env.NODE_ENV !== 'development' && user.features?.canAccessMainPayments !== true) {
    window.location.href = '/rental-manager/properties';
    return null;
  }

  return (
    <AppContextProvider value={{ isImpersonator, isMobileDevice, isPostingPathAdmin, screenWidth }}>
      <SiftScience email={user.email} isImpersonator={isImpersonator} />
      {children}
    </AppContextProvider>
  );
}

export default function App({ Component, pageProps }: AppProps) {
  useBootstrap(() => import('../bootstrap'));

  // Set Panda CSS theme for Constellation v10 visual refresh
  useEffect(() => {
    const isVisualRefreshEnabled = getBooleanConfig('isVisualRefreshEnabled', false);
    document.documentElement.setAttribute('data-panda-theme', isVisualRefreshEnabled ? 'zillow' : 'legacy-zillow');
  }, []);

  const router = useRouter();

  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useZrmPageViewEvents(trackUserEvent, router.pathname);

  return (
    <SCThemeProvider theme={ThemeConstellation}>
      <RpErrorBoundary
        fallback={
          <ErrorPage
            errorMessage="Something went wrong on our end"
            errorDetails="Try refreshing the page. If that doesn’t work, check back in a few minutes."
            action={<a href="/rental-manager/">Go to Rental Manager</a>}
            secondaryAction={<a href="/rental-manager/submit-request">Contact support</a>}
          />
        }
      >
        <ToastProvider placement="bottom-end">
          <Head applicationName={APP_NAME} buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER} />

          <QueryClientProvider client={queryClient}>
            <QueryErrorResetBoundary>
              <Auth.Provider>
                <AppAuthBoundary>
                  {(user) => (
                    <AuthenticatedApp user={user}>
                      <Layout user={user}>
                        <Component {...pageProps} />
                      </Layout>
                    </AuthenticatedApp>
                  )}
                </AppAuthBoundary>
              </Auth.Provider>
            </QueryErrorResetBoundary>
          </QueryClientProvider>
        </ToastProvider>
      </RpErrorBoundary>
    </SCThemeProvider>
  );
}
